import * as THREE from "three";

export class PlayControls_MouseLock {
  constructor(camera, avatar, setting = {}, canvas, previousCameraOffset) {
    const {
      distance = 15,
      bottomClamp = -15,
      topClamp = 70,
      sensitivityX = 0.05,
      sensitivityY = 0.05,
      cameraOffset = {},
      zoom = {},
      lookAtPoint = {},
    } = setting;
  
    const {
      x: offsetX = 0,
      y: offsetY = 5,
      z: offsetZ = 3,
    } = cameraOffset;
  
    const {
      scrollLock = true,
      minDistance = 5,
      maxDistance = 15
    } = zoom;
  
    const {
      target = "CAMERA_TARGET",
      x: lookAtX = 0,
      y: lookAtY = 0,
      z: lookAtZ = 0,
    } = lookAtPoint;
  
    this.target_ = canvas || document;
    this.camera_ = camera;
    this.avatar = avatar;
    this.previousCameraOffset = previousCameraOffset;
    this.isFPSControls = true;
    this.distance = distance;
    this.bottomClamp = Math.max(bottomClamp, -89.999);
    this.topClamp = Math.min(topClamp, 89.999);
    this.sensitivityX = sensitivityX;
    this.sensitivityY = sensitivityY;
    this.cameraOffset = { offsetX, offsetY, offsetZ };
    this.zoom = { scrollLock, minDistance, maxDistance };
    this.lookAtPoint = { target, lookAtX, lookAtY, lookAtZ };
    this._TargetYaw = 0;
    this._TargetPitch = 0;
    this.initialize_();
  }
  

  initialize_() {
    this.current_ = {
      leftButton: false,
      rightButton: false,
    };
    this.previous_ = null;
    this.keys_ = {};
    this.previousKeys_ = {};

    this.onMouseDown = this.onMouseDown_.bind(this);
    this.onMouseMove = this.onMouseMove_.bind(this);
    this.onMouseUp = this.onMouseUp_.bind(this);
    this.onKeyDown = this.onKeyDown_.bind(this);
    this.onKeyUp = this.onKeyUp_.bind(this);
    this.onPointerLockChange = this.onPointerLockChange_.bind(this);
    this.onMouseWheel = this.onMouseWheel_.bind(this);

    this.addEventListeners(false);
  }

  async requestPointerLock() {
    await this.target_.requestPointerLock();
  }

  addEventListeners(fromPointerLockChangeEvent) {
    if (fromPointerLockChangeEvent) {
      this.target_.addEventListener("mousedown", this.onMouseDown, false);
      this.target_.addEventListener("mousemove", this.onMouseMove, false);
      this.target_.addEventListener("keydown", this.onKeyDown, false);
      this.target_.addEventListener("keyup", this.onKeyUp, false);
      this.target_.addEventListener("wheel", this.onMouseWheel, false);
    } else {
      this.target_.addEventListener("mouseup", this.onMouseUp, false);
      document.addEventListener("pointerlockchange", this.onPointerLockChange, false);
    }
  }

  removeEventListeners(fromPointerLockChangeEvent) {
    this.target_.removeEventListener("mousedown", this.onMouseDown, false);
    this.target_.removeEventListener("mousemove", this.onMouseMove, false);
    this.target_.removeEventListener("keydown", this.onKeyDown, false);
    this.target_.removeEventListener("keyup", this.onKeyUp, false);
    this.target_.removeEventListener("wheel", this.onMouseWheel, false);
    if (fromPointerLockChangeEvent) return;
    this.target_.removeEventListener("mouseup", this.onMouseUp, false);
    document.removeEventListener("pointerlockchange", this.onPointerLockChange, false);
  }

  onMouseMove_(e) {
    this._TargetYaw -= e.movementX * this.sensitivityX;
    this._TargetPitch += e.movementY * this.sensitivityY;
    this._TargetPitch = Math.max(this.bottomClamp, Math.min(this.topClamp, this._TargetPitch));
  }

  onMouseDown_(e) {
    this.onMouseMove_(e);

    switch (e.button) {
      case 0: {
        this.current_.leftButton = true;
        break;
      }
      case 2: {
        this.current_.rightButton = true;
        break;
      }
      default:
        break;
    }
  }

  onMouseUp_(e) {
    this.onMouseMove_(e);

    switch (e.button) {
      case 0: {
        this.requestPointerLock();
        this.current_.leftButton = false;
        break;
      }
      case 2: {
        this.current_.rightButton = false;
        break;
      }
      default:
        break;
    }
  }

  onKeyDown_(e) {
    this.keys_[e.keyCode] = true;
  }

  onKeyUp_(e) {
    this.keys_[e.keyCode] = false;
  }

  onPointerLockChange_(e) {
    if (document.pointerLockElement === this.target_) {
      this.addEventListeners(true);
    } else {
      this.removeEventListeners(true);
    }
  }

  onMouseWheel_(e) {
    if (this.zoom.scrollLock) return;
    this.distance += e.deltaY * 0.01;
    this.distance = Math.max(this.zoom.minDistance, Math.min(this.zoom.maxDistance, this.distance));
  }

  key(keyCode) {
    return !!this.keys_[keyCode];
  }

  isReady() {
    return this.previous_ !== null;
  }

  update() {
    // Calculate the camera offset based on the player's direction
    const forwardDirection = new THREE.Vector3(
      Math.sin(THREE.MathUtils.degToRad(this._TargetYaw)),
      0,
      Math.cos(THREE.MathUtils.degToRad(this._TargetYaw))
    );
  
    const cameraOffset = new THREE.Vector3(
      forwardDirection.x * this.cameraOffset.offsetZ,
      this.cameraOffset.offsetY,
      forwardDirection.z * this.cameraOffset.offsetZ
    );
  
    // Calculate the camera position based on the player's position and the camera offset
    const cameraPositionBase = new THREE.Vector3(
      this.avatar.position.x + cameraOffset.x,
      this.avatar.position.y + cameraOffset.y,
      this.avatar.position.z + cameraOffset.z
    );
  
    // Convert spherical coordinates to Cartesian coordinates
    const pitch = THREE.MathUtils.degToRad(this._TargetPitch);
    const yaw = THREE.MathUtils.degToRad(this._TargetYaw);
  
    // Calculate the camera position in spherical coordinates
    const radius = this.distance;
    const sphericalOffset = new THREE.Vector3(
      radius * Math.cos(pitch) * Math.sin(yaw),
      radius * Math.sin(pitch),
      radius * Math.cos(pitch) * Math.cos(yaw)
    );
  
    // Adjust camera position based on the calculated offset
    const finalCameraPosition = new THREE.Vector3();
    finalCameraPosition.addVectors(cameraPositionBase, sphericalOffset);
  
    // Ensure the camera's y position respects the bottomClamp
    const minY = cameraPositionBase.y + this.distance * Math.sin(THREE.MathUtils.degToRad(this.bottomClamp));
    finalCameraPosition.y = Math.max(minY, finalCameraPosition.y);
  
    // Set the camera position
    this.camera_.position.set(finalCameraPosition.x, finalCameraPosition.y, finalCameraPosition.z);
  
    // Determine the lookAt target
    let lookAtTarget = new THREE.Vector3();
    if (this.lookAtPoint.target === "AVATAR") {
      lookAtTarget.addVectors(this.avatar.position, new THREE.Vector3(this.lookAtPoint.lookAtX, this.lookAtPoint.lookAtY, this.lookAtPoint.lookAtZ));
    } else {
      lookAtTarget.copy(cameraPositionBase);
    }
  
    // Set the camera to look at the determined target
    this.camera_.lookAt(lookAtTarget);
  }  

  dispose() {
    this.removeEventListeners(false);
    document.exitPointerLock();

    const resetPosition = new THREE.Vector3();
    resetPosition.addVectors(this.avatar.position, this.previousCameraOffset);
    this.camera_.position.copy(resetPosition);
    this.camera_.lookAt(this.avatar.position.x, this.avatar.position.y + 5, this.avatar.position.z);
  }
}

